import { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import useScheduledCalls from '../hooks/useScheduledCalls';
import {
  IconQueue, IconHistory, IconUsers, IconBuilding,
  IconPhone, IconFileText, IconCheckCircle, IconBarChart,
  IconShield, IconSettings, IconWarehouse, IconClock,
  IconWrench, IconClipboard, IconActivity, IconDollar, IconAlertTriangle
} from './Icons';
import NotificationBell from './NotificationBell';

// Chevron icon for accordion
function IconChevron({ open }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transition: 'transform 0.2s ease', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', opacity: 0.5 }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export default function Sidebar() {
  const { user, can, logout, isSuperAdmin, isAtLeastAdmin, isAtLeastManager, isConfigurator, positionName, isDriver, isDispatcher, isSales, isSalesAdmin, isMaintenance, effectiveTeam, effectiveTeamNames, legacyRole, teamSlug } = useAuth();
  const location = useLocation();
  // Tablets (iPad portrait is 744–834px) get the icon rail by default: the
  // full 260px sidebar left ~560px for content, which clipped stat strips,
  // tab rows and header buttons. The user can still expand it; the rail
  // comes back whenever the window crosses into the tablet range.
  const TABLET_QUERY = '(min-width: 769px) and (max-width: 1024px)';
  const [collapsed, setCollapsed] = useState(() => typeof window !== 'undefined' && window.matchMedia(TABLET_QUERY).matches);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(TABLET_QUERY);
    const onChange = e => setCollapsed(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange); };
  }, []);
  // The phone tab bar's "More" button opens this same drawer.
  useEffect(() => {
    const onToggle = () => setMobileOpen(o => !o);
    window.addEventListener('apollo:toggle-menu', onToggle);
    return () => window.removeEventListener('apollo:toggle-menu', onToggle);
  }, []);
  const { scheduledCount } = useScheduledCalls();
  const [workflowCount, setWorkflowCount] = useState(0);
  const [activeLeaderboardGroups, setActiveLeaderboardGroups] = useState([]);
  const [moduleConfig, setModuleConfig] = useState([]);

  const loadSidebarData = useCallback(async () => {
    try {
      const [groups, modules, wfCounts] = await Promise.all([
        api.getLeaderboardActiveGroups().catch(() => []),
        api.getModules().catch(() => []),
        api.getWorkflowItemCounts().catch(() => ({ active: 0 })),
      ]);
      setActiveLeaderboardGroups(Array.isArray(groups) ? groups : []);
      setModuleConfig(Array.isArray(modules) ? modules : []);
      setWorkflowCount(wfCounts?.active || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (user) loadSidebarData();
  }, [user, loadSidebarData]);

  // Determine which section should be open based on current route
  // Dispatch items that live under /admin/ but belong to the dispatch section
  const dispatchAdminPaths = ['/admin/drivers', '/admin/config', '/dispatch/setup'];

  function getSectionFromPath(path) {
    if (path.startsWith('/workflow')) return 'workflow';
    if (path.startsWith('/sales')) return 'sales';
    if (path.startsWith('/leaderboard')) return 'leaderboard';
    if (path.startsWith('/admin-mod/leaderboard')) return 'leaderboard';
    if (path.startsWith('/admin-mod/notifications')) return 'setup';
    if (path.startsWith('/maintenance')) return 'maintenance';
    if (path.startsWith('/admin-mod')) return 'admin';
    if (dispatchAdminPaths.some(p => path.startsWith(p))) return 'dispatch';
    if (path.startsWith('/admin')) return 'setup';
    return 'dispatch';
  }

  // Initialize from localStorage or current route
  const [openSection, setOpenSection] = useState(() => {
    const saved = localStorage.getItem('sidebar_section');
    if (saved) return saved;
    return getSectionFromPath(location.pathname);
  });

  // Auto-switch section when navigating to a different module
  useEffect(() => {
    const section = getSectionFromPath(location.pathname);
    if (section !== openSection) {
      setOpenSection(section);
      localStorage.setItem('sidebar_section', section);
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  function toggleSection(section) {
    const next = openSection === section ? null : section;
    setOpenSection(next);
    if (next) localStorage.setItem('sidebar_section', next);
  }

  if (!user) return null;

  // Identity comes from team (via AuthContext), permissions from tier
  const tier = user.permission_tier || 'user';
  // TAGGER — a field position seeded on the Sales team (Sep 3, Matt). Team
  // membership would otherwise hand them every sales-facing section, but the
  // job is one route on a phone. Hard-exclude Dispatch, Time Clock,
  // Leaderboard and Submissions the same way isDriver is excluded below;
  // My Tagging Route stays.
  const isTagger = positionName === 'Tagger';

  const tierBadgeColors = {
    super_admin: '#dc2626', admin: '#d97706', manager: '#2563eb', user: '#6b7280',
  };
  const badgeColor = tierBadgeColors[tier] || '#6b7280';
  const badgeLabel = positionName || tier.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());

  // ── MODULE VISIBILITY HELPER ───────────────────────────────────
  // Admin+ tiers always see all active modules. Everyone else: check the
  // module's visible_roles against the server-derived legacy_role, which
  // is resolved from the user's effective team chain on the server
  // (see server/utils/teamHierarchy.js resolveLegacyRoleFromTeamChain).
  //
  // DO NOT reintroduce a local `TEAM_TO_ROLE = { ... }` here. That
  // class of bug (hardcoded flat map that only knew parent teams)
  // hid the Leaderboard section for every sub-team member until
  // Apr 22 evening. The server now owns this mapping — trust it.
  function isModuleVisible(moduleKey) {
    const mod = moduleConfig.find(m => m.module_key === moduleKey);
    if (!mod) return true; // default visible if not configured yet
    if (!mod.is_active) return false;
    if (isAtLeastAdmin) return true;
    const effectiveRole = legacyRole || user.role; // server-derived, no local map
    const roles = Array.isArray(mod.visible_roles) ? mod.visible_roles : [];
    return roles.includes(effectiveRole);
  }

  // ── SECTION DEFINITIONS ──────────────────────────────────────
  // Each section: { key, label, icon, show, items[] }

  const sections = [];

  // DISPATCH — single hub entry. The hub page (/dispatch) shows
  // bucket cards for Queue, Scheduled, Driver Comms, Setup, and
  // Driver Configuration. Sub-pages remain reachable via direct URL.
  // Rendered "flat" so the DISPATCH section header itself is the
  // NavLink to /dispatch — no accordion, no second-level "Dispatch".
  if (isModuleVisible('dispatch') && !isTagger) {
    sections.push({
      key: 'dispatch',
      label: 'Dispatch',
      icon: <IconQueue />,
      show: true,
      flat: true,
      to: '/dispatch',
      end: true,
      // Scheduled count is surfaced inside the Dispatch hub buckets,
      // so the sidebar link no longer carries a duplicate badge.
      badge: null,
      items: [],
    });
  }

  // TIME CLOCK — controlled by module config like every other module.
  // Super admin can change who sees this in /admin/modules.
  // Drivers are hard-excluded (same pattern as Truck Maintenance):
  // they don't clock in here, and module config alone let them through.
  if (isModuleVisible('timeclock') && !isDriver && !isTagger) {
    const timeclockItems = [
      { to: '/timeclock', label: 'Time Clock', icon: <IconClock />, end: true, show: true },
      { to: '/my-hours', label: 'My Hours', icon: <IconHistory />, show: true },
      { to: '/pto', label: 'PTO', icon: <IconCheckCircle />, show: true },
      { to: '/admin/whos-in', label: "Who's In", icon: <IconUsers />, show: isAtLeastManager },
      { to: '/admin/time-cards', label: 'Time Cards', icon: <IconClipboard />, show: isAtLeastManager },
      { to: '/admin/payroll-report', label: 'Payroll Report', icon: <IconBarChart />, show: isAtLeastManager },
      { to: '/admin/pto', label: 'PTO Review', icon: <IconCheckCircle />, show: isAtLeastManager },
      { to: '/admin/timeclock-setup', label: 'Setup', icon: <IconSettings />, show: isSuperAdmin },
    ];
    sections.push({
      key: 'timeclock',
      label: 'TIME CLOCK',
      icon: <IconClock />,
      show: true,
      items: timeclockItems,
    });
  }

  // TRUCK MAINTENANCE — controlled by module config.
  // Hidden from drivers entirely until Matt rolls out driver maintenance
  // obligations; restore the driver item set (inspect / work-orders /
  // fleet-status / submit / report) from git history when that lands.
  if (isModuleVisible('maintenance') && !isDriver) {
    // Sales-module sidebar pattern: only Overview + Setup. The 6 buckets
    // (Work Orders / Incidents / Accidents / Inspections / Preventive
    // Maintenance / Fleet Status) are reached by clicking cards on the
    // Overview page. Keeps the nav clean and forces users through the
    // KPI-rich landing.
    const maintItems = [
      { to: '/maintenance', label: 'Overview', icon: <IconWrench />, end: true, show: true },
      { to: '/maintenance/setup', label: 'Setup', icon: <IconSettings />, show: isConfigurator('maintenance') || can('manage_trucks') || can('manage_shops') || can('manage_maintenance') },
    ];
    sections.push({
      key: 'maintenance',
      label: 'TRUCK MAINTENANCE',
      icon: <IconWrench />,
      show: true,
      items: maintItems.filter(i => i.show),
    });
  }

  // LEADERBOARD — dynamic based on active groups + module config.
  // Use server-derived team_slug (see server/utils/teamHierarchy.js).
  // Do NOT reintroduce a local slugifyTeam() here.
  const userGroup = teamSlug || (isAtLeastAdmin ? 'admin' : null);
  const userHasBoard = userGroup && activeLeaderboardGroups.includes(userGroup);
  const showLeaderboard = isModuleVisible('leaderboard') && !isTagger && (userHasBoard || can('manage_admin'));

  if (showLeaderboard) {
    const leaderboardItems = [];

    if (can('manage_admin')) {
      leaderboardItems.push(
        { to: '/leaderboard', label: 'Boards', icon: <IconBarChart />, end: true, show: true },
        { to: '/leaderboard/setup', label: 'Setup', icon: <IconSettings />, show: isConfigurator('leaderboard') },
      );
    } else if (userHasBoard) {
      // Non-admins get direct link to their board
      leaderboardItems.push({
        to: `/leaderboard/${userGroup}`,
        label: 'My Leaderboard',
        icon: <IconBarChart />,
        show: true,
      });
    }

    sections.push({
      key: 'leaderboard',
      label: 'LEADERBOARD',
      icon: <IconBarChart />,
      show: true,
      items: leaderboardItems.filter(i => i.show),
    });
  }

  // ADMIN MODULE — controlled by module config
  if (isModuleVisible('admin')) {
    sections.push({
      key: 'admin',
      label: 'ADMIN',
      icon: <IconShield />,
      show: true,
      items: [
        { to: '/admin-mod/employees', label: 'Employees', icon: <IconUsers />, show: true },
        { to: '/admin-mod/company', label: 'Company', icon: <IconBuilding />, show: true },
        { to: '/admin-mod/facilities', label: 'Facilities', icon: <IconWarehouse />, show: true },
        { to: '/admin-mod/compliance', label: 'Compliance', icon: <IconCheckCircle />, show: true },
        { to: '/admin-mod/issue-templates', label: 'Issue Templates', icon: <IconAlertTriangle />, show: true },
        { to: '/admin/grading-report', label: 'Grading Report', icon: <IconBarChart />, show: can('manage_admin') },
        { to: '/admin/storage', label: 'Storage', icon: <IconWarehouse />, show: isSuperAdmin },
        { to: '/admin/data-pulls', label: 'Data Pull Log', icon: <IconActivity />, show: isSuperAdmin },
        { to: '/admin/timecard-review', label: 'Timecard Review', icon: <IconClock />, show: isAtLeastManager },
        { to: '/admin/payroll', label: 'Payroll', icon: <IconDollar />, show: isAtLeastAdmin },
        { to: '/admin/pins', label: 'Dispatcher PINs', icon: <IconShield />, show: isAtLeastManager },
        { to: '/admin/checklists', label: 'Shift Checklists', icon: <IconClipboard />, show: isAtLeastManager },
        { to: '/admin/pto', label: 'PTO Review', icon: <IconCheckCircle />, show: isAtLeastManager },
        { to: '/admin/payroll-settings', label: 'Payroll Settings', icon: <IconSettings />, show: isSuperAdmin },
      ],
    });
  }

  // TASK BOARD — cross-module personal inbox
  {
    const taskBoardItems = [
      { to: '/workflow', label: 'Task Board', icon: <IconActivity />, end: true, show: true, badge: workflowCount > 0 ? workflowCount : null },
      { to: '/workflow/report-issue', label: 'Report Issue', icon: <IconAlertTriangle />, show: true },
      { to: '/workflow/items', label: 'All Items', icon: <IconClipboard />, show: can('manage_admin') || can('manage_tasks') },
    ];
    sections.push({
      key: 'workflow',
      label: 'TASK BOARD',
      icon: <IconActivity />,
      show: true,
      items: taskBoardItems.filter(i => i.show),
    });
  }

  // SALES — controlled by module config. Drivers hard-excluded: their
  // one sales-adjacent need (reporting a sign issue) lives at the top
  // of the sidebar as Report Sign Issue.
  if (isModuleVisible('sales') && !isDriver) {
    const salesItems = [
      // Expense sidebar entries retired May 20 Phase 2.2. Submit Expense
      // is now a linked card in the 'Requests' group on the Requests
      // overview. The Expenses history (super_admin only) is reached
      // from the 'History' button on the Submit Expense form.
      { to: '/sales/requests', label: 'Submissions', icon: <IconFileText />, show: !isTagger },
      // Sign Request Phase 2 (Jul 27): the manager's day-planning board and
      // the technician's phone-first route view.
      { to: '/sales/requests/sign_request/schedule', label: 'Sign Schedule', icon: <IconFileText />, show: isAtLeastAdmin || positionName === 'Sign Manager' },
      { to: '/sign-route', label: 'My Sign Route', icon: <IconFileText />, show: isAtLeastAdmin || positionName === 'Sign Manager' || positionName === 'Sign Technician' },
      // Tagging Request (Aug 30): same two surfaces, one prefix over.
      { to: '/sales/requests/tagging_request/schedule', label: 'Tagging Schedule', icon: <IconFileText />, show: isAtLeastAdmin || positionName === 'Tagging Manager' },
      { to: '/tagging-route', label: 'My Tagging Route', icon: <IconFileText />, show: isAtLeastAdmin || positionName === 'Tagging Manager' || positionName === 'Tagger' },
      // 'New Accounts' sidebar entry retired May 20 Phase 2. The 4 items
      // that lived on /sales/accounts (Upload Agreement, Property Info
      // Sheet, Service Agreement, Generate Notices) are now reachable
      // from the New Accounts group on the Requests overview page.
      // Underlying routes (/sales/accounts/new, /sales/property-info,
      // /sales/service-agreement, /sales/notices, /sales/accounts/list)
      // still work for deep-links.
      //
      // Step 2 of the accounts migration adds a separate 'Accounts (CRM)'
      // sidebar link below — read-only browser for the unified accounts
      // table. Super-admin only until the accounts_page_enabled flag is on.
      { to: '/accounts', label: 'Accounts (CRM)', icon: <IconBuilding />, show: isSuperAdmin },
      // 'Notices' sidebar entry retired. Notice generation is reached
      // via the Notices linked card under New Accounts, and the history
      // page is opened from the 'History' button on the Notice Generator.
      // AUDIT MODULE — sidebar entries retired May 20 Phase 2.1. New Audit
      // is now reached via the Parking Audit bucket on the Requests page.
      // The Audits history page (/sales/audits) is now opened from the
      // History button on the New Parking Audit wizard header. Server
      // scopes Audits history results to the user's reports-to chain
      // (visibilityScope in audits.js), so reps only see audits they
      // created or reports submitted to them.
      // DIRECTORY — DFW Property Directory (sales tool, sister-property pitches).
      // Sales-only per Matt 2026-05-17: only Sales positions + admins see it.
      // Server enforces the same gate (canViewDirectory in routes/sales/directory.js).
      { to: '/sales/directory', label: 'Directory', icon: <IconBuilding />, show: isSales || isSalesAdmin || isAtLeastAdmin },
      // FWAA Business Exchange — event intelligence board (Aug 6, 2026).
      // Same audience as Directory; server enforces in routes/sales/fwaa.js.
      { to: '/sales/fwaa', label: 'FWAA Exchange', icon: <IconUsers />, show: isSales || isSalesAdmin || isAtLeastAdmin },
      { to: '/sales/setup', label: 'Setup', icon: <IconSettings />, show: isConfigurator('sales') },
    ];
    sections.push({
      key: 'sales',
      label: 'SALES',
      icon: <IconDollar />,
      show: true,
      items: salesItems.filter(i => i.show),
    });
  }

  // COMPANY SETUP — managers+ get Properties, admin+ get full setup
  if ((isAtLeastManager || isDispatcher || can('manage_properties')) && isModuleVisible('setup')) {
    const setupItems = [];

    if (isAtLeastAdmin) {
      setupItems.push(
        { to: '/admin/users', label: 'Users', icon: <IconUsers />, show: can('manage_users') },
      );
    }

    setupItems.push(
      { to: '/admin/properties', label: 'Properties', icon: <IconBuilding />, show: can('manage_properties') },
    );

    if (isAtLeastAdmin) {
      setupItems.push(
        { to: '/admin/teams-positions', label: 'Teams & Positions', icon: <IconSettings />, show: true },
        { to: '/admin-mod/notifications', label: 'Notifications', icon: <IconPhone />, show: true },
        // Property Grading config — manage_admin gate enforced server-side too
        { to: '/setup/grading', label: 'Property Grading', icon: <IconBarChart />, show: can('manage_admin') },
      );
    }

    // Module Setup + Settings — super_admin only
    if (isSuperAdmin) {
      setupItems.push(
        { to: '/admin/modules', label: 'Module Setup', icon: <IconShield />, show: true },
        { to: '/admin/migration', label: 'Migration Console', icon: <IconActivity />, show: true },
        { to: '/admin/settings', label: 'Settings', icon: <IconSettings />, show: true },
      );
    }

    const filtered = setupItems.filter(i => i.show);
    if (filtered.length > 0) {
      sections.push({
        key: 'setup',
        label: 'COMPANY SETUP',
        icon: <IconSettings />,
        show: true,
        items: filtered,
      });
    }
  }

  // Sort sections by module_config sort_order
  function getModuleSortOrder(sectionKey) {
    const mod = moduleConfig.find(m => m.module_key === sectionKey);
    return mod ? mod.sort_order : 999;
  }
  sections.sort((a, b) => getModuleSortOrder(a.key) - getModuleSortOrder(b.key));

  const visibleSections = sections.filter(s => s.show);

  // If only one section, keep it always open (no need for accordion)
  const alwaysOpen = visibleSections.length === 1;

  return (
    <>
      {/* Mobile top bar */}
      <div className="mobile-topbar">
        <button className="hamburger" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? '\u2715' : '\u2630'}
        </button>
        <img src={`${process.env.PUBLIC_URL}/logo-banner.png`} alt="Apollo Towing DFW" className="mobile-logo" />
        <div className="mobile-user">
          <NotificationBell />
          <span className="role-badge" style={{ background: badgeColor }}>
            {badgeLabel}
          </span>
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <img src={`${process.env.PUBLIC_URL}/logo-banner.png`} alt="Apollo Towing DFW" className="sidebar-logo" />
          <div className="sidebar-header-actions">
            <NotificationBell />
            <button className="sidebar-collapse-btn" onClick={() => setCollapsed(!collapsed)} title={collapsed ? 'Expand' : 'Collapse'}>
              {collapsed ? '\u25B6' : '\u25C0'}
            </button>
          </div>
        </div>

        <nav className="sidebar-nav">
          {/* Home — always first, always visible. Goes to the role-aware dashboard. */}
          <div className="nav-section">
            <div className="nav-section-items open">
              <NavLink to="/" end className="sidebar-link">
                <IconBarChart />
                <span>Home</span>
              </NavLink>
              {/* Sign quick-report — DRIVERS ONLY (Matt, Aug 31). Drivers submit
                  what they see on their rounds; reps raise sign work through the
                  Sign Request form, so this link is noise on their sidebar.
                  isDriver covers Driver / Day Driver / Night Driver; the team
                  check is a fallback for a driver whose position was never set
                  (prod position data is uneven post-migration). The /sign-report
                  route itself is unchanged and still reachable by URL. */}
              {(isDriver || effectiveTeamNames.includes('Driver Operations')) && (
                <NavLink to="/sign-report" className="sidebar-link" onClick={() => setMobileOpen(false)}>
                  <IconAlertTriangle />
                  <span>Report Sign Issue</span>
                </NavLink>
              )}
              {/* Gate access report — drivers only, same gate as the sign
                  quick-report above. /gate-report stays reachable by URL. */}
              {(isDriver || effectiveTeamNames.includes('Driver Operations')) && (
                <NavLink to="/gate-report" className="sidebar-link" onClick={() => setMobileOpen(false)}>
                  <IconShield />
                  <span>Gate Access Problem</span>
                </NavLink>
              )}
            </div>
          </div>

          {visibleSections.map(section => {
            // Flat section — render the section label itself as a NavLink.
            // Used by Dispatch so the sidebar header is the hub entry,
            // skipping the accordion + redundant inner item.
            if (section.flat && section.to) {
              return (
                <div key={section.key} className="nav-section">
                  <div className="nav-section-items open">
                    <NavLink
                      to={section.to}
                      end={section.end}
                      className="sidebar-link"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span className="sidebar-icon">{section.icon}</span>
                      <span className="sidebar-label">{section.label}</span>
                      {section.badge && <span className="sidebar-badge">{section.badge}</span>}
                    </NavLink>
                  </div>
                </div>
              );
            }

            const isOpen = alwaysOpen || openSection === section.key;

            return (
              <div key={section.key} className="nav-section">
                <button
                  className={`nav-section-title nav-section-toggle ${isOpen ? 'open' : ''}`}
                  onClick={() => !alwaysOpen && toggleSection(section.key)}
                  style={alwaysOpen ? { cursor: 'default' } : {}}
                >
                  <span>{section.label}</span>
                  {!alwaysOpen && <IconChevron open={isOpen} />}
                </button>

                <div className={`nav-section-items ${isOpen ? 'open' : ''}`}>
                  {section.items.map(item => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className="sidebar-link"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span className="sidebar-icon">{item.icon}</span>
                      <span className="sidebar-label">{item.label}</span>
                      {item.badge && <span className="sidebar-badge">{item.badge}</span>}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <span className="role-badge" style={{ background: badgeColor }}>
              {badgeLabel}
            </span>
            <span className="sidebar-username">{user.full_name || user.username}</span>
          </div>
          <button className="btn btn-sm sidebar-logout" onClick={logout}>Logout</button>
        </div>
      </aside>
    </>
  );
}
